import React from 'react';
import { deriveReceiptTip, formatTipWithPercent } from '../../orders/tip/tip.utils';
import type { OrderView } from '../../../../../services/orders/orders.service';
import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import { formatMoney as formatPosMoney, roundMoney } from '../../../../../utils/money';
import classes from './order-summary.module.scss';
import { getReceiptDiscountTotal } from '../shared/receipt.utils';

type OrderSummaryProps = {
  order?: OrderView | null;
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  discount: number;
  itemTotalMode?: 'before-discount' | 'after-discount';
  fixed?: boolean;
  showDiscount?: boolean;
};

const OrderSummaryComponent: React.FC<OrderSummaryProps> = ({
  order,
  orderlines,
  paymentlines,
  discount,
  itemTotalMode = 'before-discount',
  fixed = true,
  showDiscount = true,
}) => {
  const formatMoney = (value: number) => formatPosMoney(value, order?.roundAmounts ?? false);
  // Take-away bills its unsent (pending) lines too (paid upfront, sent to kitchen at close).
  const isTakeAway = order?.orderType === 'take_away';
  // Canceled lines carry a price but are voided — never bill them (fully-voided orders total 0).
  const sentOrderlines = orderlines.filter(
    (line) => line.status !== 'canceled' && (line.status !== 'pending' || isTakeAway),
  );
  const grossItemsTotal = sentOrderlines
    .reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity, order?.roundAmounts), 0);
  const itemsTotal = sentOrderlines
    .reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), order?.roundAmounts), 0);

  // Global tip resolves against the billable total (items net of per-line + order-level discounts).
  const billableTotal = Math.max(0, itemsTotal - discount);
  const { tip, paidTip } = deriveReceiptTip(order, paymentlines, billableTotal);

  // "Paid" includes the tip collected on done lines (frozen share + own tip), not just the bill amount.
  const paid = paymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce((sum, line) => sum + line.amount, 0) + paidTip;

  const remaining = itemsTotal + tip - discount - paid;
  const unpaidBeforeDiscount = grossItemsTotal + tip - paid;
  const totalDiscount = getReceiptDiscountTotal(sentOrderlines, discount, order?.roundAmounts);
  // A closed order is settled (paid == billable) — never warn, even with a discount.
  const isSettled = order?.status === 'closed';
  const shouldWarnRemaining = !isSettled
    && (remaining > 0.001 || (totalDiscount > 0.001 && unpaidBeforeDiscount > 0.001));
  const showsAfterDiscountItemsTotal = itemTotalMode === 'after-discount';

  return (
    <div className={`${classes.summaryCard} ${fixed ? '' : classes.summaryCardStatic}`} data-testid="order-summary">
      <div className={classes.grid}>
        <div className={classes.cellRight}>
          {showsAfterDiscountItemsTotal ? (
            <>
              <span className={classes.cellLabel}>
                <span>עלות פריטים</span>
                <span>(אחרי הנחה)</span>
              </span>
              <span className={classes.cellValue}>{formatMoney(billableTotal)} ₪</span>
            </>
          ) : (
            <>
              <span className={classes.cellLabel}>
                <span>עלות פריטים</span>
                <span>(לפני הנחה)</span>
              </span>
              <span className={classes.cellValue}>{formatMoney(grossItemsTotal)} ₪</span>
            </>
          )}
        </div>
        <div className={classes.cellLeft}>
          <span className={classes.cellLabel}>טיפ</span>
          <span className={`${classes.cellValue} ${classes.tipValue}`} dir="ltr">{formatTipWithPercent(tip, billableTotal, order?.roundAmounts ?? false)}</span>
        </div>
        <div className={classes.cellRight}>
          {showDiscount && <>
            <span className={classes.cellLabel}>הנחה</span>
            <span className={classes.cellValue}>{formatMoney(totalDiscount)} ₪</span>
          </>}
        </div>
        <div className={classes.cellLeft}>
          <span className={classes.cellLabel}>שולם</span>
          <span className={classes.cellValue}>{formatMoney(paid)} ₪</span>
        </div>
      </div>

      <div
        className={`${classes.remainingRow} ${shouldWarnRemaining ? classes.remainingRowPositive : classes.remainingRowZero}`}
        data-testid="order-remaining"
      >
        <span className={classes.remainingLabel}>יתרה לתשלום (סה&quot;כ)</span>
        <span className={classes.remainingValue}>{formatMoney(remaining)} ₪</span>
      </div>
    </div>
  );
};

export const OrderSummary = React.memo(OrderSummaryComponent);
